import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Feedback as FeedbackIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useFeedback } from "../../hooks/useFeedback/useFeedback";

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FeedbackFormData {
  subject: string;
  message: string;
  category: string;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<FeedbackFormData>({
    subject: "",
    message: "",
    category: "general",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { submitFeedback } = useFeedback();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const categories = [
    { value: "general", label: "General Feedback" },
    { value: "bug", label: "Bug Report" },
    { value: "feature", label: "Feature Request" },
    { value: "ui", label: "UI/UX Issue" },
    { value: "performance", label: "Performance Issue" },
  ];

  const handleInputChange =
    (field: keyof FeedbackFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.subject.trim() || !formData.message.trim()) {
      setSubmitStatus("error");
      setErrorMessage("Please fill in both subject and message fields.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    try {
      // Send feedback to backend API using the feedback hook
      const result = await submitFeedback({
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        category: formData.category,
      });

      if (result instanceof Error) {
        throw result;
      }

      setSubmitStatus("success");

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({ subject: "", message: "", category: "general" });
        setSubmitStatus("idle");
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmitStatus("error");
      setErrorMessage("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ subject: "", message: "", category: "general" });
      setSubmitStatus("idle");
      setErrorMessage("");
      onClose();
    }
  };

  const content = (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      {submitStatus === "success" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Thank you for your feedback! We'll review it and get back to you if
          needed.
        </Alert>
      )}

      {submitStatus === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <TextField
        select
        fullWidth
        label="Category"
        value={formData.category}
        onChange={handleInputChange("category")}
        SelectProps={{
          native: true,
        }}
        sx={{ mb: 2, mt: 1 }}
        disabled={isSubmitting}
      >
        {categories.map((category) => (
          <option key={category.value} value={category.value}>
            {category.label}
          </option>
        ))}
      </TextField>

      <TextField
        fullWidth
        label="Subject"
        value={formData.subject}
        onChange={handleInputChange("subject")}
        placeholder="Brief description of your feedback"
        sx={{ mb: 2 }}
        disabled={isSubmitting}
        required
      />

      <TextField
        fullWidth
        multiline
        rows={4}
        label="Message"
        value={formData.message}
        onChange={handleInputChange("message")}
        placeholder="Please provide detailed feedback, including steps to reproduce if reporting a bug..."
        sx={{ mb: 2 }}
        disabled={isSubmitting}
        required
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your feedback helps us improve Pickem. We appreciate you taking the time
        to share your thoughts!
      </Typography>

      <DialogActions sx={{ px: 0, pt: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={
            isSubmitting || !formData.subject.trim() || !formData.message.trim()
          }
          startIcon={
            isSubmitting ? <CircularProgress size={16} /> : <SendIcon />
          }
          sx={{ minWidth: 120 }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog open={open} onClose={handleClose} fullScreen>
        <AppBar
          position="static"
          color="default"
          elevation={0}
          sx={{ position: "relative" }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
              disabled={isSubmitting}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1, ml: 1 }}>
              Submit Feedback
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 2, pb: 10 }}>{content}</Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
        }}
      >
        <FeedbackIcon color="primary" />
        Submit Feedback
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3 }}>{content}</DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
